import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Upload, FileArrowUp, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const SYSTEM_FIELDS = [
  { key: "account_number", label: "Account number", required: true },
  { key: "trade_ticket", label: "Trade ticket", required: true },
  { key: "symbol", label: "Symbol", required: false },
  { key: "lot_volume", label: "Lot volume", required: true },
  { key: "open_time", label: "Open time", required: false },
  { key: "close_time", label: "Close time", required: true },
  { key: "profit", label: "Profit", required: false },
  { key: "trade_status", label: "Trade status", required: true },
];

function autoGuess(columns) {
  const map = {};
  const norm = (s) => s.toLowerCase().replace(/[^a-z]/g, "");
  for (const f of SYSTEM_FIELDS) {
    const candidates = {
      account_number: ["login", "account", "accountnumber", "account_id", "accountid"],
      trade_ticket: ["ticket", "tradeid", "tradeticket", "ordernum", "order"],
      symbol: ["symbol", "instrument", "pair"],
      lot_volume: ["volume", "lot", "lots", "size"],
      open_time: ["opentime", "openat", "opened"],
      close_time: ["closetime", "closeat", "closed"],
      profit: ["profit", "pnl"],
      trade_status: ["status", "tradestatus"],
    }[f.key] || [];
    const found = columns.find((c) => candidates.includes(norm(c)));
    if (found) map[f.key] = found;
  }
  return map;
}

export default function UploadReport() {
  const [step, setStep] = useState(1);
  const [upload, setUpload] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [templateName, setTemplateName] = useState("");

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpload(data);
      setMapping(autoGuess(data.columns));
      setStep(2);
      toast.success(`Uploaded ${data.total_rows} rows`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const runImport = async () => {
    const required = SYSTEM_FIELDS.filter((f) => f.required).map((f) => f.key);
    for (const r of required) {
      if (!mapping[r]) {
        toast.error(`Missing mapping for ${r}`);
        return;
      }
    }
    setImporting(true);
    try {
      const { data } = await api.post(`/uploads/${upload.id}/import`, {
        upload_id: upload.id,
        mapping,
        mapping_template_name: templateName || null,
      });
      setResult(data);
      setStep(3);
      toast.success(`Imported ${data.matched_rows} valid rows`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setImporting(false);
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <PageHeader
        title="Upload Broker Report"
        subtitle="Upload broker Excel or CSV reports. Map columns, then process trades into the campaign."
        breadcrumbs="Imports / Upload"
        testId="upload-page"
      />

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6 text-xs font-mono">
        {["Upload", "Map columns", "Summary"].map((s, i) => (
          <React.Fragment key={s}>
            <div className={`px-3 py-1.5 rounded-full border ${step === i + 1 ? "bg-blue-500/10 border-blue-500 text-blue-300" : step > i + 1 ? "bg-emerald-500/10 border-emerald-500 text-emerald-300" : "border-white/10 text-gray-500"}`}>
              {step > i + 1 && "✓ "} {s}
            </div>
            {i < 2 && <ArrowRight size={12} className="text-gray-600" />}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <div className="bg-[#111827] border border-white/10 rounded-lg p-12 text-center">
          <FileArrowUp size={56} weight="duotone" className="mx-auto text-blue-400 mb-4" />
          <h2 className="font-display text-xl font-bold text-white mb-2">Upload a broker report</h2>
          <p className="text-sm text-gray-400 mb-6">CSV or Excel (.xlsx, .xls). Max 50MB.</p>
          <label data-testid="upload-input-label" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md cursor-pointer">
            <Upload size={16} /> Choose file
            <input data-testid="upload-input" type="file" accept=".csv,.xlsx,.xls" onChange={handleUpload} className="hidden" />
          </label>
        </div>
      )}

      {step === 2 && upload && (
        <div className="space-y-6">
          <div className="bg-[#111827] border border-white/10 rounded-lg p-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs uppercase tracking-widest text-gray-500">File</p>
              <p className="text-xs text-gray-500 font-mono">{upload.total_rows} rows · {upload.columns.length} columns</p>
            </div>
            <p className="font-display text-lg font-bold text-white">{upload.file_name}</p>
          </div>

          <div className="bg-[#111827] border border-white/10 rounded-lg p-5">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Map columns</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {SYSTEM_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-3">
                  <div className="w-40 flex-shrink-0">
                    <p className="text-sm text-white">{f.label} {f.required && <span className="text-orange-400">*</span>}</p>
                    <p className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">{f.key}</p>
                  </div>
                  <select
                    data-testid={`map-${f.key}`}
                    value={mapping[f.key] || ""}
                    onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })}
                    className="flex-1 bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
                  >
                    <option value="">— select column —</option>
                    {upload.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-3">
              <input
                placeholder="Optional: save as mapping template (e.g. MT5 standard)"
                data-testid="map-template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="flex-1 bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm text-white outline-none"
              />
            </div>
          </div>

          <div className="bg-[#111827] border border-white/10 rounded-lg overflow-hidden">
            <p className="text-xs uppercase tracking-widest text-gray-500 px-5 pt-4">Preview (first 20 rows)</p>
            <div className="overflow-x-auto max-h-80 mt-2">
              <table className="w-full text-xs">
                <thead className="bg-[#1F2937]/60 sticky top-0">
                  <tr>
                    {upload.columns.map((c) => (
                      <th key={c} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-400">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {upload.preview.map((row, i) => (
                    <tr key={i} className="border-b border-white/5">
                      {upload.columns.map((c) => (
                        <td key={c} className="px-3 py-1.5 text-gray-300 font-mono whitespace-nowrap max-w-[200px] truncate">{row[c]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={() => { setStep(1); setUpload(null); }} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button
              data-testid="run-import-btn"
              onClick={runImport}
              disabled={importing}
              className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-md shadow-[0_4px_20px_rgba(249,115,22,0.3)] disabled:opacity-50"
            >
              {importing ? "Processing…" : "Process Import →"}
            </button>
          </div>
        </div>
      )}

      {step === 3 && result && (
        <div className="bg-[#111827] border border-white/10 rounded-lg p-8" data-testid="import-summary">
          <CheckCircle size={48} weight="duotone" className="text-emerald-400 mb-3" />
          <h2 className="font-display text-2xl font-bold text-white mb-1">Import complete</h2>
          <p className="text-sm text-gray-400 mb-6">All eligible trades have been counted toward the campaign.</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat label="Matched rows" value={result.matched_rows} color="text-emerald-400" />
            <Stat label="Unmatched" value={result.unmatched_rows} color="text-gray-300" />
            <Stat label="Duplicate" value={result.duplicate_rows} color="text-gray-300" />
            <Stat label="Invalid" value={result.invalid_rows} color="text-red-400" />
            <Stat label="Total valid lot" value={result.total_valid_lot_imported.toFixed(2)} color="text-blue-400" />
            <Stat label="Participants updated" value={result.participants_updated} color="text-orange-400" />
            <Stat label="Milestones triggered" value={result.milestones_triggered} color="text-orange-400" />
          </div>

          {result.errors && result.errors.length > 0 && (
            <div className="mt-4 p-4 rounded-md bg-red-500/10 border border-red-500/30">
              <p className="text-xs uppercase tracking-widest text-red-300 mb-2">Errors</p>
              <ul className="text-xs text-red-200 space-y-0.5 font-mono">{result.errors.slice(0, 10).map((e, i) => <li key={i}>· {e}</li>)}</ul>
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <button onClick={() => { setStep(1); setUpload(null); setResult(null); }} data-testid="upload-another-btn" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md">Upload another</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-[#0B0F19] border border-white/5 rounded-md p-4">
      <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
      <p className={`font-mono text-2xl font-bold mt-1 ${color}`}>{value}</p>
    </div>
  );
}
